/* eslint-disable */
import React, { useState, useContext, useEffect, useRef } from 'react';
import { Data, Filters } from './contexts';
import LoaderGraphic from '../utils/LoaderGraphic';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { createPopper } from '@popperjs/core';
import Modal from 'react-modal';
import moment from 'moment';
import { ChatContainer, CopilotPDF } from './ChatContainer';
import ReportTabsPopover from './ReportTabsPopover';
import FunctionalExpenses from './FunctionalExpenses';
import PnlByClass from './PnlByClass';
import CustomQuarterOrMonthYTDReport from './report_components/CustomQuarterOrMonthYTDReport';
import MoneyReportSheets from './report_components/MoneyReportSheets';
import MoneyCashFlow from './report_components/MoneyCashFlow';
import MoneyKFI from './report_components/MoneyKFI';
import { dentalPNLFormat, doctorPNLFormat } from './NAICSFocusedReport/NAICSFocusedReport';
import MonthlyHistoricTable from './monthly/MonthlyHistoricTable';
import MonthlyHistoricCashFlow from './monthly/MonthlyHistoricCashFlow';
import MoneyMonthlyKFI from './monthly/MoneyMonthlyKFI';
import MoneyYTD from './monthly/MoneyYTD';
import MoneyYTDCashFlow from './monthly/MoneyYTDCashFlow';
import MoneyYTDKFI from './monthly/MoneyYTDKFI';
import RevenueAnalysis from './RevenueAnalysis';
import OpexAnalysis from './OpexAnalysis';
import ParentDashboard from './ParentDashboard';
import EditParentChildDashboard from './utils/EditParentChildDashboard';
import TextEditor from './TextEditor';
import TextEditorPDF from './TextEditorPDF';
import DashboardViewModal from './utils/DashboardViewModal';
import DashboardViewControls from './utils/DashboardViewControls';
import { getDashBoardPagesPDF } from './PDFDashboardHelper';
import { peopleCustomersT12Metrics } from './report_components/PeopleCustomersHelpers';
import BigTableChartsForSheets from './report_components/BigTableChartsForSheets';
import PeopleCustomersKPIs from './report_components/PeopleCustomersKPIs';
import MonthlyKPITable from './MonthlyKPITable';

const naicsPNLTitle = (naicsCode, entityType) => {
  if (naicsCode.startsWith('6212')) {
    return 'Dental P&L'
  } else if (naicsCode.startsWith('6211') || naicsCode.startsWith('6213')) {
    return 'Doctor P&L'
  } else {
    return null
  }
}

const getYTDMonthCount = (data, ytdMonths) => {
  if (!data || !Array.isArray(ytdMonths)) return 0;
  return ytdMonths.filter((m) => _.isNumber(data[m])).length;
};

const monthsObjBuilder = (fiscalYearStartMonth = 1, reportPeriod = 'Trailing 12', year = null, trailing12EndMonths = null) => {
  const monthNames = {
    1: 'JAN',
    2: 'FEB',
    3: 'MAR',
    4: 'APR',
    5: 'MAY',
    6: 'JUN',
    7: 'JUL',
    8: 'AUG',
    9: 'SEP',
    10: 'OCT',
    11: 'NOV',
    12: 'DEC',
  };
  const monthsObj = {};

  if (reportPeriod === 'Trailing 12') {
    const months = Object.values(monthNames);
    let endMonth = trailing12EndMonths?.[year]
    const startMonths = months.splice(endMonth);
    let nextMonth = 1;

    startMonths.forEach((f, i) => {
      monthsObj[i + 1] = `${f} ${Number(year - 1)}`;
      nextMonth++;
    });

    months.forEach((f, i) => {
      monthsObj[nextMonth] = `${f} ${year}`;
      nextMonth++;
    });
  } else if (fiscalYearStartMonth !== 1) {
    const months = Object.values(monthsObj);
    const startMonths = months.splice(fiscalYearStartMonth - 1);
    const fiscalMonths = [...startMonths, ...months];
    fiscalMonths.forEach((f, i) => {
      monthsObj[i + 1] = f;
    });
  } else {
    return monthNames;
  }
  return monthsObj;
};

const compareWithRowKeys = {
  None: null,
  Practice: 'practice_avg',
  Peers: 'avg',
  NSCHBC: 'nschbc',
  'Peers - Top 10% Fastest Growing': 'top_10_revenue_growth',
  'Peers - Top 25% Fastest Growing': 'top_25_revenue_growth',
  'Peers - Top 10% Most Profitable': 'top_10_net_profit',
  'Peers - Top 25% Most Profitable': 'top_25_net_profit'
};

const createCompareWithTitle = (compareWith) => {
  if (compareWith) {
    const colonIndex = compareWith.indexOf(':');
    return compareWith.slice(colonIndex + 1);
  } else {
    return '';
  }
};

const listOfPeopleCustomersMetrics = [
  'average_benefits',
  'average_wages_and_salaries',
  'cost_of_turnover',
  'customer_roi',
  'gross_profit',
  'gross_profit_per_customer',
  'number_of_employees_who_left',
  'number_of_new_customers_needed_to_maintain_growth',
  'operating_expenses_net_income',
  'operating_expenses_net_income_per_customer',
  'operating_expenses_net_income_per_employee',
  'revenue_per_customer',
  'sum_compensation_growth_rate',
  'sum_compensation_per_employee',
  'sum_health_insurance_per_employee',
  'sum_payroll_per_employee',
  'sum_retirement_per_employee',
  'sum_subcontractors_growth_rate',
  'sum_taxes_and_benefits_growth_rate',
  'sum_taxes_and_benefits_per_employee',
  'total_customer_costs_common_size_revenue',
  'total_number_of_customers',
  'total_number_of_employees',
  'total_revenue',
  'total_revenue_per_employee',
  'training_per_employee',
  'turnover_rate',
];

const MoneyReport = React.forwardRef((props, ref) => {
  const { calcs, monthly_calcs, trailing12, ytd_months, forecastCalcs } = useContext(Data);
  const { filters, filterOptions } = useContext(Filters);
  const [activeModal, setActiveModal] = useState('');
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);
  const popperInstance = useRef(null);
  const { navbar } = window.navbar;
  let previousYearYTDYouTotalRevenue;
  let currentYearYTDYouTotalRevenue;
  let firstYearMonth;
  let secondYearMonth;
  let formattedFirstYearMonth;
  let formattedSecondYearMonth = null;
  const firmName = props.name ? props.name.slice(0, props.name.indexOf('/') - 1) : '';
  const naicsFocusedReport = naicsPNLTitle(props.naicsCode, props.entityType);
  const pnlRows = props.naicsCode.startsWith('6212')
    ? dentalPNLFormat
    : props.naicsCode.startsWith('6211') || props.naicsCode.startsWith('6213')
      ? doctorPNLFormat
      : null;

  useEffect(() => {
    if (props.reportTabsPopoverOpen && buttonRef.current && popoverRef.current) {
      popperInstance.current = createPopper(buttonRef.current, popoverRef.current, {
        placement: 'bottom', // Default position
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 8], // 0px horizontally, 8px vertically from the reference
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 12,
            },
          },
          {
            name: 'shift', // shifts popover into view if it's overflowing
            options: {
              padding: 8,
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['bottom-start', 'bottom-end'],
            },
          },
        ],
      });
    }

    return () => popperInstance.current?.destroy();
  }, [props.reportTabsPopoverOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (props.tabNameModelIsOpen) {
        return;
      }

      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        props.setReportTabsPopoverOpen(false);
      }
    }

    if (props.reportTabsPopoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [props.reportTabsPopoverOpen, props.tabNameModelIsOpen]);

  const kpiValues = (page, year, monthlyCalcs, aggCalcs, displayColumnsBy, compareWith) => {
    const youMetrics = {};
    const compareMetrics = {};
    const youPreviousYearMetricsYTD = {};
    const comparePreviousYearMetricsYTD = {};

    if (displayColumnsBy === 'Months') {
      listOfPeopleCustomersMetrics.forEach((metric) => {
        Object.entries(
          peopleCustomersT12Metrics(
            page,
            metric,
            monthlyCalcs.you,
            year,
            year - 1,
            year - 2,
          ),
        ).forEach(([key, value]) => {
          youMetrics[key] = value;
        });

        if (compareWith === 'Default:Peers' || compareWith === 'Peers') {
          Object.entries(
            peopleCustomersT12Metrics(
              page,
              metric,
              aggCalcs,
              year,
              year - 1,
              year - 2,
            ),
          ).forEach(([key, value]) => {
            compareMetrics[key] = value;
          });
        } else {
          Object.entries(
            peopleCustomersT12Metrics(
              page,
              metric,
              monthlyCalcs.you,
              year - 1,
              year - 2,
              year - 3,
            ),
          ).forEach(([key, value]) => {
            compareMetrics[key] = value;
          });
        }
      });
    } else if (displayColumnsBy === 'Year to Date') {
      listOfPeopleCustomersMetrics.forEach((metric) => {
        Object.entries(
          peopleCustomersT12Metrics(
            page,
            metric,
            monthlyCalcs.you,
            year,
            year - 1,
            year - 2,
            true,
            ytd_months,
            'you'
          ),
        ).forEach(([key, value]) => {
          youMetrics[key] = value;
        });

        Object.entries(
          peopleCustomersT12Metrics(
            page,
            metric,
            aggCalcs,
            year,
            year - 1,
            year - 2,
            true,
            ytd_months,
            'avg'
          ),
        ).forEach(([key, value]) => {
          compareMetrics[key] = value;
        });
      });

      listOfPeopleCustomersMetrics.forEach((metric) => {
        Object.entries(
          peopleCustomersT12Metrics(
            page,
            metric,
            monthlyCalcs.you,
            year - 1,
            year - 2,
            year - 3,
            true,
            ytd_months
          ),
        ).forEach(([key, value]) => {
          youPreviousYearMetricsYTD[key] = value;
        });
        Object.entries(
          peopleCustomersT12Metrics(
            page,
            metric,
            aggCalcs,
            year - 1,
            year - 2,
            year - 3,
            true,
            ytd_months
          ),
        ).forEach(([key, value]) => {
          comparePreviousYearMetricsYTD[key] = value;
        });
      });

      const youSelectedYearTotalRevenue = monthlyCalcs?.you?.total_revenue?.[year];
      const youPreviousYearTotalRevenue = monthlyCalcs?.you?.total_revenue?.[year - 1];
      const firstYearMonth = getYTDMonthCount(youPreviousYearTotalRevenue, ytd_months)
      const secondYearMonth = getYTDMonthCount(youSelectedYearTotalRevenue, ytd_months)
      const compareFirstYearMonth = firstYearMonth < 10 ? '0' + firstYearMonth.toString() : firstYearMonth;
      const compareSecondYearMonth = secondYearMonth < 10 ? '0' + secondYearMonth.toString() : secondYearMonth;

      formattedFirstYearMonth = compareFirstYearMonth
      formattedSecondYearMonth = compareSecondYearMonth
    }

    return {
      youMetrics,
      compareMetrics,
      youPreviousYearMetricsYTD,
      comparePreviousYearMetricsYTD,
    };
  };

  const openVideoModal = (reportVal) => {
    setActiveModal(reportVal);
    setVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setVideoModalOpen(false);
  };

  const hrefBuilder = (subdomain, path) => {
    const domainSplit = document.location.host.split('.');
    domainSplit[0] = subdomain;
    return `${location.protocol}//${domainSplit.join('.')}${path}`;
  };

  const VideoModal = () => {
    let modalPic = '';
    let modalTitle = '';
    let videoLink = null;
    let modalClass = activeModal === 'general' ? 'report-credit-modal-content' : 'video-modal-content'

    if (activeModal === 'dashboard') {
      modalTitle = !props.planAccess.money.dashboard
        ? 'Upgrade to Access the Dashboard'
        : 'Unlock Report to Access the Dashboard';
      videoLink = 'https://www.youtube.com/embed/F-iBTc4462k?autoplay=1&mute=1';
    }

    if (activeModal === 'ai') {
      modalTitle = !props.planAccess.money.ai
        ? 'Upgrade to Access the Peerview Copilot'
        : 'Unlock Report to Access the Peerview Copilot';
      modalPic = require('../../../assets/images/PremiumPeerviewCopilot.png');
    }

    if (activeModal === 'kfis') {
      modalTitle = !props.planAccess.money.kfis
        ? "Upgrade to Access the KFI's"
        : "Unlock Report to Access the KFI's";
      modalPic = require('../../../assets/images/PremiumKFIs.png');
    }

    if (activeModal === 'general') {
      modalTitle = 'Unlock Reports to Access This Feature';
    }

    const manageSubscriptionsURL = hrefBuilder(navbar.subdomain, '/subscriptions');
    const unlockReportsURL = hrefBuilder(navbar.subdomain, navbar.unlock_reports_url);
    const linkText = navbar.report_credits ? 'Unlock Reports' : 'Buy More Report Credits';
    const purchaseText = 'Buy more report credits.';

    return (
      <Modal
        className={{
          base: modalClass,
          afterOpen: `${modalClass}_after-open hide-on-print`,
          beforeClose: `${modalClass}_before-close hide-on-print`,
        }}
        overlayClassName={{
          base: 'overlay-base hide-on-print',
          afterOpen: 'overlay-base_after-open hide-on-print',
          beforeClose: 'overlay-base_before-close hide-on-print',
        }}
        isOpen={videoModalOpen}
        onRequestClose={closeVideoModal}
        shouldCloseOnOverlayClick
        closeTimeoutMS={0}
        contentLabel="Video About Report Features"
        ariaHideApp={false}
      >
        <div>
          <h1>{modalTitle}</h1>
          {videoLink ? (
            <iframe
              width="560"
              height="315"
              src={videoLink}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) 
          : modalPic ? (
            <img className="premium-pic-popup" src={modalPic} />
          ) : null }
          {((props.planAccess.money.dashboard && !navbar.reports_unlocked) ||
            (props.planAccess.money.kfis && !navbar.reports_unlocked) || 
            (activeModal === 'general' && !navbar.reports_unlocked)) ? (
            <div className="unlock-report-container">
              <p>
                {`You have ${navbar.report_credits} Report Credits left.`}{' '}
                <a className="btn btn-primary unlock-report-btn" href={unlockReportsURL}>
                  {linkText}
                </a>
              </p>
            </div>
          ) : null}
          <a className="btn btn-primary manage-subscription-btn" href={manageSubscriptionsURL}>
            Manage Your Subscription
          </a>
          <p>Click anywhere outside to close this pop-up.</p>
        </div>
      </Modal>
    );
  }

  const handleActiveTab = (section, addToTabs = false) => {
    let checkIfPreference = null;
    if (section.key.includes('default_') || section.key.includes('custom_')) {
      checkIfPreference = props.allReportTabs.find((t) => t.key === section.key);
      if (checkIfPreference) {
        section = checkIfPreference;
      }
    }

    if (section.display_on_tab && section.active_view && addToTabs) {
      section.update = false;
      addToTabs = false;
    } else if (section.display_on_tab && !section.active_view && addToTabs) {
      section.update = true;
      section.active_view = true;
      addToTabs = false;
    } else if (addToTabs) {
      section.update = true;
      section.display_on_tab = true;
      section.active_view = true;
    } else {
      section.update = true;
      section.active_view = true;
    }

    switch (section.report) {
      case 'peerview_copilot':
        if (
          navbar.user_type === 'Admin' ||
          (navbar.reports_unlocked && props.planAccess.money.ai)
        ) {
          props.addReportToTabs(section, addToTabs);
        } else if (!props.planAccess.money.ai && navbar.user_type !== 'Admin') {
          openVideoModal('ai');
        } else if (
          !navbar.reports_unlocked &&
          props.planAccess.money.ai &&
          navbar.user_type !== 'Admin'
        ) {
          openVideoModal('ai');
        }
        break;
      case 'kfis':
        if (
          navbar.user_type === 'Admin' ||
          (navbar.reports_unlocked && props.planAccess.money.kfis)
        ) {
          props.addReportToTabs(section, addToTabs);
        } else if (!props.planAccess.money.kfis && navbar.user_type !== 'Admin') {
          openVideoModal('kfis');
        } else if (
          !navbar.reports_unlocked &&
          props.planAccess.money.kfis &&
          navbar.user_type !== 'Admin'
        ) {
          openVideoModal('kfis');
        }
        break;
      case 'dashboard':
        const planType = navbar.plan_type;
        if (
          navbar.user_type === 'Admin' ||
          (navbar.reports_unlocked && props.planAccess.money.dashboard)
        ) {
          props.addReportToTabs(section, addToTabs);
        } else if (!props.planAccess.money.dashboard && navbar.user_type !== 'Admin') {
          openVideoModal('dashboard');
        } else if (
          !navbar.reports_unlocked &&
          props.planAccess.money.dashboard &&
          navbar.user_type !== 'Admin'
        ) {
          openVideoModal('dashboard');
        }
        break;
      default:
        if (
          (section.key.includes('default_') && !checkIfPreference) ||
          (section.key.includes('custom_') && !checkIfPreference)
        ) {
          props.handleDefaultDashboardViewTab(section);
        } else {
          props.addReportToTabs(section, addToTabs);
        }
        break;
    }
    props.setReportTabsPopoverOpen(false);
  };

  const closeSurveyModal = () => {
    props.setSurveyModalOpen(false);
  };

  const getReactKey = (key, pdf, fallback) => (key ? (pdf ? `${key}-pdf` : key) : fallback);

  // Doctors and Dentists (NAICS 621)
  const getDoctorsDentistsReport = (key, pdf, format, reportPeriod, monthlyCompareWithTitle, aggregateData, tabName, tabFilters, yearRange, forecastVals, monthsObj) => {
    const reactKey = getReactKey(key, pdf, 'docs_pnl');
    if (format === 'annual') {
      const year = tabFilters ? tabFilters.year : filters.year;
      return (
        <MoneyReportSheets
          key={reactKey}
          aggregateData={aggregateData}
          aggregateYTDDate={props.aggregateYTDDate}
          clientName={props.clientName}
          commonKey="revenue"
          companyYTDDate={props.companyYTDDate}
          calendarYearEnd={props.calendarYearEnd}
          fiscalYearEnd={props.fiscalYearEnd}
          firmName={firmName}
          n={props.docs_n}
          naicsCode={props.naicsCode}
          page={pdf ? 'docs_pnl-moneypdf' : 'docs_pnl'}
          pnlTree={props.pnlTree}
          rows={pnlRows}
          sheet="pnl"
          tabName={tabName}
          tabFilters={tabFilters}
          title={naicsFocusedReport}
          year={year}
          yearRange={yearRange}
        />
      );
    }
    if (format === 'monthly' && reportPeriod !== 'CM + YTD') {
      return (
        <MonthlyHistoricTable
          key={reactKey}
          aggregateData={aggregateData}
          clientName={props.clientName}
          calendarYearEnd={props.calendarYearEnd}
          firmName={firmName}
          monthlyCompareWithTitle={monthlyCompareWithTitle}
          forecastVals={forecastVals}
          monthsObj={monthsObj}
          n={props.docs_n}
          naicsCode={props.naicsCode}
          page="docs_pnl"
          pdf={pdf}
          rows={pnlRows}
          sheet="pnl"
          tabName={tabName}
          tabFilters={tabFilters}
          title={naicsFocusedReport}
        />
      );
    }
    if (format === 'ytd' || reportPeriod === 'CM + YTD') {
      return (
        <MoneyYTD
          key={reactKey}
          aggregateData={aggregateData}
          clientName={props.clientName}
          firmName={firmName}
          forecastVals={forecastVals}
          monthlyCompareWithTitle={monthlyCompareWithTitle}
          n={props.docs_n}
          naicsCode={props.naicsCode}
          page="docs_pnl"
          pdf={pdf}
          rows={pnlRows}
          sheet="pnl"
          tabName={tabName}
          tabFilters={tabFilters}
          title={naicsFocusedReport}
        />
      );
    }
    return null;
  };

  // Nonprofit (NAICS 813 or nonprofit entity)
  const getFunctionalExpensesReport = (key, pdf, format, aggregateData, tabName, tabFilters) => {
    const reactKey = getReactKey(key, pdf, 'functional_expenses');
    if (format === 'annual') {
      const compareWith = tabFilters ? tabFilters.compare_with : filters.compare_with;
      const year = tabFilters ? tabFilters.year : filters.year;
      return (
        <FunctionalExpenses
          key={reactKey}
          aggregateData={aggregateData}
          aggregateYTDDate={props.aggregateYTDDate}
          calcs={calcs}
          clientName={props.clientName}
          companyYTDDate={props.companyYTDDate}
          compareWith={compareWith}
          calendarYearEnd={props.calendarYearEnd}
          fiscalYearEnd={props.fiscalYearEnd}
          firmName={firmName}
          n={props.n}
          page={pdf ? 'functional_expenses-moneypdf' : 'functional_expenses'}
          pdf={pdf}
          sheet="pnl"
          tabName={tabName}
          tabFilters={tabFilters}
          tree={props.tree}
          title={naicsFocusedReport}
          year={year}
        />
      );
    }
    return (
      <div key={reactKey} className="money-report-card">
        <div className="money-report-pdf">
          <div className="money-report-container">
            <p>This report will only display for Years view.</p>
          </div>
        </div>
      </div>
    );
  };

  const getNAICSFocusedReport = (key, pdf = false, format = '', reportPeriod = '', monthlyCompareWithTitle, aggregateData, tabName = '', tabFilters = null, yearRange = [], forecastVals = {}, monthsObj = {}) => {
    const reactKey = key ? (pdf ? `${key}-pdf` : key) : 'naics_focused';

    if (props.naicsCode.startsWith('621')) {
      return getDoctorsDentistsReport(reactKey, pdf, format, reportPeriod, monthlyCompareWithTitle, aggregateData, tabName, tabFilters, yearRange, forecastVals, monthsObj);
    }
    if (props.naicsCode.startsWith('813') || props.entityType === 'nonprofit') {
      return getNonprofitReport(reactKey, pdf, format, aggregateData, tabName, tabFilters);
    }
    return null;
  };

  const getYearRange = (year, reportPeriod, youCalcs = {}) => {
    let years = []
    if (reportPeriod === 'All Years') {
      years = youCalcs?.total_revenue ? Object.keys(youCalcs?.total_revenue ).sort((a, b) => a - b) : []
    } else {
      let numberOfYears = parseInt(reportPeriod) - 1;
      for (let i = numberOfYears; i > -1; i--) {
        years.push(year - i)
      }
    }
    return years
  }

  const getReportComponent = (tab, pdf = false) => {
    const tabFilters = (tab.details && Object.keys(tab.details).length > 0) ? tab.details : filters;
    const aggregateData = tab?.aggregates
    const displayColumnsBy = tabFilters.display_columns_by;
    const reportPeriod = tabFilters.report_period;
    const yearRange = getYearRange(tabFilters.year, reportPeriod, calcs?.you)
    const compareWith = tabFilters.compare_with;
    const calcsAvgType = compareWithRowKeys[compareWith];
    const monthlyCompareWithTitle = createCompareWithTitle(compareWith);
    const forecastName = compareWith && compareWith.includes("Forecast") ? compareWith : "Default";
    const forecastVals = forecastCalcs[forecastName]?.data
    const realForecastName = forecastCalcs[forecastName]?.name

    const monthsObj = monthsObjBuilder(
      props.fiscalYearStartMonth,
      tabFilters.report_period,
      tabFilters.year,
      trailing12?.["recent_month"]
    );
    let monthLabels = props.calendarYearEnd
      ? ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
      : [
        'Month 1',
        'Month 2',
        'Month 3',
        'Month 4',
        'Month 5',
        'Month 6',
        'Month 7',
        'Month 8',
        'Month 9',
        'Month 10',
        'Month 11',
        'Month 12',
      ];
    if (tabFilters.report_period === 'Trailing 12') {
      monthLabels = Object.values(monthsObj);
    }

    switch (tab.report) {
      case 'cover_page':
        return <CoverPage key="cover_page" />;
      case 'peerview_copilot':
        if (pdf) {
          return <CopilotPDF key="copilot-pdf" chat={props.chat} />;
        }
        return (
          <ChatContainer
            key="copilot"
            companyNotes={props.companyNotes}
            onChangeNotes={props.onChangeNotes}
            chatLoader={props.chatLoader}
            allChats={props.allChats}
            changeChat={props.changeChat}
            chat={props.chat}
            chatLoading={props.chatLoading}
            handleCopilotCall={props.handleCopilotCall}
            handleActiveTab={handleActiveTab}
            navbarInfo={navbar}
          />
        );

        break;
      case 'pnl':
        if (reportPeriod === 'CM + YTD') {
          return (
            <MoneyYTD
              key={pdf ? `mom-ytd-pnl-pdf-${tab.id}` : `mom-ytd-pnl-${tab.id}`}
              aggregateData={aggregateData}
              clientName={props.clientName}
              editMetrics={props.reportMetrics.editMetrics}
              forecastVals={forecastVals}
              monthlyCompareWithTitle={monthlyCompareWithTitle}
              firmName={firmName}
              handleEditMetrics={props.handleEditMetrics}
              handleSelectAndLayout={props.handleSelectAndLayout}
              n={props.n}
              naicsCode={props.naicsCode}
              page="money"
              pdf={pdf}
              rows={props.reportMetrics.pnl}
              sheet="pnl"
              tabName={tab.name}
              tabFilters={tabFilters}
              title="Profit & Loss"
            />
          );
        }

        if (displayColumnsBy === 'Months' && reportPeriod !== 'CM + YTD') {
          return (
            <MonthlyHistoricTable
              key={pdf ? `monthly-pnl-pdf-${tab.id}` : `monthly-pnl-${tab.id}`}
              aggregateData={aggregateData}
              calendarYearEnd={props.calendarYearEnd}
              clientName={props.clientName}
              editMetrics={props.reportMetrics.editMetrics}
              firmName={firmName}
              monthlyCompareWithTitle={monthlyCompareWithTitle}
              forecastVals={forecastVals}
              handleEditMetrics={props.handleEditMetrics}
              handleSelectAndLayout={props.handleSelectAndLayout}
              monthsObj={monthsObj}
              n={props.n}
              naicsCode={props.naicsCode}
              page="money"
              pdf={pdf}
              sheet="pnl"
              tabName={tab.name}
              tabFilters={tabFilters}
              title="Profit & Loss"
              rows={props.reportMetrics.pnl}
            />
          );
        }
        if (displayColumnsBy === 'Years') {
          if (reportPeriod === 'Year to Date') {
            return (
              <MoneyYTD
                key={pdf ? `ytd-pnl-pdf-${tab.id}` : `ytd-pnl-${tab.id}`}
                aggregateData={aggregateData}
                clientName={props.clientName}
                editMetrics={props.reportMetrics.editMetrics}
                forecastVals={forecastVals}
                firmName={firmName}
                handleEditMetrics={props.handleEditMetrics}
                handleSelectAndLayout={props.handleSelectAndLayout}
                n={props.n}
                naicsCode={props.naicsCode}
                page="money"
                pdf={pdf}
                rows={props.reportMetrics.pnl}
                sheet="pnl"
                tabName={tab.name}
                tabFilters={tabFilters}
                title="Profit & Loss"
              />
            );
          }
          return (
            <MoneyReportSheets
              key={pdf ? `money-pnl-pdf-${tab.id}` : `money-pnl-${tab.id}`}
              aggregateData={aggregateData}
              aggregateYTDDate={props.aggregateYTDDate}
              clientName={props.clientName}
              commonKey="revenue"
              companyYTDDate={props.companyYTDDate}
              editMetrics={props.reportMetrics.editMetrics}
              handleEditMetrics={props.handleEditMetrics}
              handleSelectAndLayout={props.handleSelectAndLayout}
              calendarYearEnd={props.calendarYearEnd}
              fiscalYearEnd={props.fiscalYearEnd}
              firmName={firmName}
              n={props.n}
              naicsCode={props.naicsCode}
              page={props.pdf ? 'moneypdf' : 'money'}
              rows={props.reportMetrics.pnl}
              sheet="pnl"
              tabName={tab.name}
              tabFilters={tabFilters}
              title="Profit & Loss"
              tree={props.pnlTree}
              year={tabFilters.year}
              yearRange={yearRange}
            />
          );
        }
        break;
      case 'ty_v_ly_q':
      case 'act_v_for_q':
        const title = tab.key.includes('ty_v_ly_q') ? 'This Year vs. Last Year' : `Actual vs Forecast: ${realForecastName}`;

        return (
          <CustomQuarterOrMonthYTDReport
            key={pdf ? `pdf-${tab.key}` : tab.key}
            clientName={props.clientName}
            firmName={firmName}
            forecastName={realForecastName}
            forecastVals={forecastVals}
            naicsCode={props.naicsCode}
            pdf={pdf}
            quarterMonths={props.quarterMonths}
            reportPeriod={'quarter'}
            rows={props.reportMetrics.pnl}
            sheet="pnl"
            tabName={tab.name}
            tabFilters={tabFilters}
            tabKey={tab.key}
            title={title}
            year={tabFilters.year}
          />
        );
        break;
      case 'ty_v_ly_month':
      case 'act_v_for_month':
        const monthTitle = tab.key.includes('ty_v_ly_month') ? "Month vs. Last Year" : `Month vs Forecast: ${realForecastName}`;

        return (
          <CustomQuarterOrMonthYTDReport
            key={pdf ? `pdf-${tab.key}` : tab.key}
            clientName={props.clientName}
            firmName={firmName}
            forecastName={realForecastName}
            forecastVals={forecastVals}
            naicsCode={props.naicsCode}
            pdf={pdf}
            quarterMonths={props.quarterMonths}
            reportPeriod={'month'}
            rows={props.reportMetrics.pnl}
            sheet="pnl"
            tabName={tab.name}
            tabFilters={tabFilters}
            tabKey={tab.key}
            title={monthTitle}
            year={tabFilters.year}
          />
        );
        break;
      case 'docs_pnl':
        if (displayColumnsBy === 'Months') {
          return getDoctorsDentistsReport(tab.key, pdf, 'monthly', reportPeriod, monthlyCompareWithTitle, aggregateData, tab.name, tabFilters, [], forecastVals, monthsObj);
        }
        if (displayColumnsBy === 'Years') {
          if (reportPeriod === 'Year to Date') {
            return getDoctorsDentistsReport(tab.key, pdf, 'ytd', reportPeriod, monthlyCompareWithTitle, aggregateData, tab.name, tabFilters);
          }
          return getDoctorsDentistsReport(tab.key, pdf, 'annual', reportPeriod, monthlyCompareWithTitle, aggregateData, tab.name, tabFilters, yearRange);
        }
        break;
      case 'functional_expenses':
        if (displayColumnsBy === 'Years' && reportPeriod !== 'Year to Date') {
          return getFunctionalExpensesReport(tab.key, pdf, 'annual', aggregateData, tab.name, tabFilters);
        }
        // Months view, or Years + YTD, shows the "Years view only" message
        return getFunctionalExpensesReport(tab.key, pdf, '', aggregateData, tab.name, tabFilters);
        break;
      case 'pnl_by_class_qbo':
        return (
          <PnlByClass
            key={pdf ? `${tab.id}-pnl_by_class_qbo-pdf` : `${tab.id}-pnl_by_class_qbo`}
            calcs={calcs}
            aggregateData={aggregateData}
            aggregateYTDDate={props.aggregateYTDDate}
            clientName={props.clientName}
            companyYTDDate={props.companyYTDDate}
            calendarYearEnd={props.calendarYearEnd}
            fiscalYearEnd={props.fiscalYearEnd}
            firmName={firmName}
            forecastName={realForecastName}
            forecastVals={forecastVals}
            naicsCode={props.naicsCode}
            pdf={pdf}
            rows={props.reportMetrics.pnl}
            tabName={tab.name}
            tabFilters={tabFilters}
            title='P&L by Class'
            tree={props.tree}
            year={tabFilters.year}
          />
        )
        break;
      case 'balance-sheet':
        if (reportPeriod === 'CM + YTD') {
          return (
              <MoneyYTD
                key={pdf ? `mom-ytd-balance-sheet-pdf-${tab.id}` : `mom-ytd-balance-sheet-${tab.id}`}
                aggregateData={aggregateData}
                clientName={props.clientName}
                editMetrics={props.reportMetrics.editMetrics}
                forecastVals={forecastVals}
                monthlyCompareWithTitle={monthlyCompareWithTitle}
                firmName={firmName}
                handleEditMetrics={props.handleEditMetrics}
                handleSelectAndLayout={props.handleSelectAndLayout}
                n={props.n}
                page="money"
                pdf={pdf}
                rows={props.reportMetrics.balance_sheet}
                sheet="balance_sheet"
                tabName={tab.name}
                tabFilters={tabFilters}
                title="Balance Sheet"
              />
          )
        }

        if (displayColumnsBy === 'Months' && reportPeriod !== 'CM + YTD') {
          return (
            <MonthlyHistoricTable
              key={pdf ? `monthly-balance-sheet-pdf-${tab.id}` : `monthly-balance-sheet-${tab.id}`}
              aggregateData={aggregateData}
              calendarYearEnd={props.calendarYearEnd}
              clientName={props.clientName}
              editMetrics={props.reportMetrics.editMetrics}
              firmName={firmName}
              monthlyCompareWithTitle={monthlyCompareWithTitle}
              forecastVals={forecastVals}
              handleEditMetrics={props.handleEditMetrics}
              handleSelectAndLayout={props.handleSelectAndLayout}
              monthsObj={monthsObj}
              n={props.n}
              naicsCode={props.naicsCode}
              page="money"
              pdf={pdf}
              sheet="balance_sheet"
              tabName={tab.name}
              tabFilters={tabFilters}
              title="Balance Sheet"
              rows={props.reportMetrics.balance_sheet}
            />
          );
        }
        if (displayColumnsBy === 'Years') {
          if (reportPeriod === 'Year to Date') {
            return (
              <MoneyYTD
                key={pdf ? `ytd-balance-sheet-pdf-${tab.id}` : `ytd-balance-sheet-${tab.id}`}
                aggregateData={aggregateData}
                clientName={props.clientName}
                editMetrics={props.reportMetrics.editMetrics}
                forecastVals={forecastVals}
                firmName={firmName}
                handleEditMetrics={props.handleEditMetrics}
                handleSelectAndLayout={props.handleSelectAndLayout}
                n={props.n}
                page="money"
                pdf={pdf}
                rows={props.reportMetrics.balance_sheet}
                sheet="balance_sheet"
                tabName={tab.name}
                tabFilters={tabFilters}
                title="Balance Sheet"
              />
            );
          }
          return (
            <MoneyReportSheets
              key={pdf ? `money-balance-sheet-pdf-${tab.id}` : `money-balance-sheet-${tab.id}`}
              aggregateData={aggregateData}
              aggregateYTDDate={props.aggregateYTDDate}
              clientName={props.clientName}
              commonKey="assets"
              companyYTDDate={props.companyYTDDate}
              editMetrics={props.reportMetrics.editMetrics}
              handleEditMetrics={props.handleEditMetrics}
              handleSelectAndLayout={props.handleSelectAndLayout}
              calendarYearEnd={props.calendarYearEnd}
              fiscalYearEnd={props.fiscalYearEnd}
              firmName={firmName}
              n={props.n}
              page={props.pdf ? 'moneypdf' : props.page}
              rows={props.reportMetrics.balance_sheet}
              sheet="balance_sheet"
              tabName={tab.name}
              tabFilters={tabFilters}
              title="Balance Sheet"
              tree={props.balTree}
              year={tabFilters.year}
              yearRange={yearRange}
            />
          );
        }
        break;
      case 'cash-flow':
        if (reportPeriod === 'CM + YTD') {
          return (
            <MoneyYTDCashFlow
              key={tab.id}
              aggregateData={aggregateData}
              clientName={props.clientName}
              editMetrics={props.reportMetrics.editMetrics}
              forecastVals={forecastVals}
              monthlyCompareWithTitle={monthlyCompareWithTitle}
              firmName={firmName}
              handleEditMetrics={props.handleEditMetrics}
              handleSelectAndLayout={props.handleSelectAndLayout}
              n={props.n}
              pdf={pdf}
              rows={props.reportMetrics.cash_flow}
              tabName={tab.name}
              tabFilters={tabFilters}
            />
          );
        }

        if (displayColumnsBy === 'Months' && reportPeriod !== 'CM + YTD') {
          return (
            <MonthlyHistoricCashFlow
              key={tab.id}
              aggregateData={aggregateData}
              calendarYearEnd={props.calendarYearEnd}
              clientName={props.clientName}
              editMetrics={props.reportMetrics.editMetrics}
              firmName={firmName}
              monthlyCompareWithTitle={monthlyCompareWithTitle}
              forecastVals={forecastVals}
              handleEditMetrics={props.handleEditMetrics}
              handleSelectAndLayout={props.handleSelectAndLayout}
              monthsObj={monthsObj}
              n={props.n}
              page={props.page}
              pdf={pdf}
              rows={props.reportMetrics.cash_flow}
              tabName={tab.name}
              tabFilters={tabFilters}
            />
          );
        }
        if (displayColumnsBy === 'Years') {
          if (reportPeriod === 'Year to Date') {
            // Only two years being used for YTD tables.
            // Each month with values should have total revenue. Use to check how many months with values are available and label the YTD columns with the month/year.
            // const previousYearYTDYouTotalRevenue = monthlyCalcs[props.year - 1] ? monthlyCalcs[props.year - 1]['you']['total_revenue'] : undefined;
            // const currentYearYTDYouTotalRevenue = monthlyCalcs[props.year] ? monthlyCalcs[props.year]['you']['total_revenue'] : undefined;
            // let firstYearMonth = previousYearYTDYouTotalRevenue ? Object.values(previousYearYTDYouTotalRevenue).filter(value => _.isNumber(value)).length : '';
            // let secondYearMonth = currentYearYTDYouTotalRevenue ? Object.values(currentYearYTDYouTotalRevenue).filter(value => _.isNumber(value)).length : '';
            // let formattedFirstYearMonth = firstYearMonth < 10 ? '0' + firstYearMonth.toString() : firstYearMonth;
            // let formattedSecondYearMonth = secondYearMonth < 10 ? '0' + secondYearMonth.toString() : secondYearMonth;
            return (
              <MoneyYTDCashFlow
                key={tab.id}
                aggregateData={aggregateData}
                clientName={props.clientName}
                editMetrics={props.reportMetrics.editMetrics}
                forecastVals={forecastVals}
                monthlyCompareWithTitle={monthlyCompareWithTitle}
                firmName={firmName}
                handleEditMetrics={props.handleEditMetrics}
                handleSelectAndLayout={props.handleSelectAndLayout}
                n={props.n}
                pdf={pdf}
                rows={props.reportMetrics.cash_flow}
                tabName={tab.name}
                tabFilters={tabFilters}
              />
            );
          }
          return (
            <MoneyCashFlow
              key={tab.id}
              aggregateData={aggregateData}
              aggregateYTDDate={props.aggregateYTDDate}
              clientName={props.clientName}
              companyYTDDate={props.companyYTDDate}
              editMetrics={props.reportMetrics.editMetrics}
              handleEditMetrics={props.handleEditMetrics}
              handleSelectAndLayout={props.handleSelectAndLayout}
              calendarYearEnd={props.calendarYearEnd}
              fiscalYearEnd={props.fiscalYearEnd}
              firmName={firmName}
              n={props.n}
              pdf={pdf}
              title="Cash Flow"
              rows={props.reportMetrics.cash_flow}
              tabName={tab.name}
              tabFilters={tabFilters}
              year={tabFilters.year}
              yearRange={yearRange}
            />
          );
        }
        break;
      case 'kfis':
        if (reportPeriod === 'CM + YTD') {
          return (
            <MoneyYTDKFI
              key={tab.id}
              aggregateData={aggregateData}
              clientName={props.clientName}
              editMetrics={props.reportMetrics.editMetrics}
              forecastVals={forecastVals}
              monthlyCompareWithTitle={monthlyCompareWithTitle}
              firmName={firmName}
              handleEditMetrics={props.handleEditMetrics}
              handleSelectAndLayout={props.handleSelectAndLayout}
              n={props.n}
              pdf={pdf}
              rows={props.reportMetrics.kfis}
              tabName={tab.name}
              tabFilters={tabFilters}
              yearRange={[tabFilters.year - 1, tabFilters.year]}
            />
          );
        }

        if (displayColumnsBy === 'Months' && reportPeriod !== 'CM + YTD') {
          return (
            <MoneyMonthlyKFI
              key={tab.id}
              aggregateData={aggregateData}
              calendarYearEnd={props.calendarYearEnd}
              clientName={props.clientName}
              editMetrics={props.reportMetrics.editMetrics}
              firmName={firmName}
              monthlyCompareWithTitle={monthlyCompareWithTitle}
              forecastVals={forecastVals}
              handleEditMetrics={props.handleEditMetrics}
              handleSelectAndLayout={props.handleSelectAndLayout}
              monthsObj={monthsObj}
              n={props.n}
              page={props.page}
              pdf={pdf}
              rows={props.reportMetrics.kfis}
              tabName={tab.name}
              tabFilters={tabFilters}
            />
          );
        }
        if (displayColumnsBy === 'Years') {
          if (reportPeriod === 'Year to Date') {
            return (
              <MoneyYTDKFI
                key={tab.id}
                aggregateData={aggregateData}
                clientName={props.clientName}
                editMetrics={props.reportMetrics.editMetrics}
                forecastVals={forecastVals}
                firmName={firmName}
                handleEditMetrics={props.handleEditMetrics}
                handleSelectAndLayout={props.handleSelectAndLayout}
                n={props.n}
                pdf={pdf}
                rows={props.reportMetrics.kfis}
                tabName={tab.name}
                tabFilters={tabFilters}
                yearRange={[tabFilters.year - 1, tabFilters.year]}
              />
            );
          }
          return (
            <MoneyKFI
              key={tab.id}
              aggregateData={aggregateData}
              aggregateYTDDate={props.aggregateYTDDate}
              clientName={props.clientName}
              companyYTDDate={props.companyYTDDate}
              editMetrics={props.reportMetrics.editMetrics}
              handleEditMetrics={props.handleEditMetrics}
              handleSelectAndLayout={props.handleSelectAndLayout}
              calendarYearEnd={props.calendarYearEnd}
              fiscalYearEnd={props.fiscalYearEnd}
              firmName={firmName}
              naicsCode={props.naicsCode}
              n={props.n}
              pdf={pdf}
              rows={props.reportMetrics.kfis}
              tabName={tab.name}
              tabFilters={tabFilters}
              title={`KFI's`}
              year={tabFilters.year}
              yearRange={yearRange}
            />
          );
        }
        break;
      case 'company_notes':
        const cNoteForTab = props.companyNotes.find(note =>
          tab?.original_company_note_id
            ? note?.id === tab.original_company_note_id
            : note?.preference_id === tab?.id   // fallback for non report view tabs
        )

        if (props.pdf) {
          if (cNoteForTab) {
            return (
                <TextEditorPDF
                  key={`${tab.key}-pdf`}
                  companyNotes={cNoteForTab}
                />
              )
          }
        } else {
          if (cNoteForTab) {
            return (
              <TextEditor
                key={`${tab.key}-${props.companyNotes.length}`}
                companyNotes={cNoteForTab}
                onChangeNotes={props.onChangeNotes}
              />
            )
          }
        }
        break;
      case 'revenue_analysis':
        return (
          <RevenueAnalysis
            key={pdf ? `${tab.key}-pdf` : tab.key}
            aggregateData={aggregateData}
            forecastName={realForecastName}
            forecastVals={forecastVals}
            naicsCode={props.naicsCode}
            pdf={pdf}
            tabName={tab.name}
            tabFilters={tabFilters}
          />
        )
        break;
      case 'opex_analysis':
        return (
          <OpexAnalysis
            key={pdf ? `${tab.key}-pdf` : tab.key}
            aggregateData={aggregateData}
            forecastName={realForecastName}
            forecastVals={forecastVals}
            pdf={pdf}
            tabName={tab.name}
            tabFilters={tabFilters}
            tree={props.tree}
          />
        )
        break;
      case 'monthly_kpi_table':
        return (
          <MonthlyKPITable
            key={pdf ? `${tab.key}-pdf` : tab.key}
            aggregateData={aggregateData}
            calendarYearEnd={props.calendarYearEnd}
            forecastName={realForecastName}
            forecastVals={forecastVals}
            naicsCode={props.naicsCode}
            pdf={pdf}
            tabName={tab.name}
            tabFilters={tabFilters}
          />
        )
      case 'dashboard':
        if (props.pdf) {
          let naicsCode = tabFilters.code;
          let dataType = tabFilters.datatype;
          let revenueBand = tabFilters.revenue_band;
          let region = tabFilters.region;
          let filtersTitle;
          if (displayColumnsBy === 'Months' || reportPeriod === 'Year to Date') {
            const compareParts = tabFilters.compare_with.split(':');
            let compare = compareParts.length > 1 ? compareParts.slice(1).join(':').trim() : tabFilters.compare_with;
            filtersTitle = `NAICS: ${naicsCode}, Data Type: ${dataType}, Revenue: ${revenueBand}, Region: ${region}, Report Period: ${reportPeriod}, Compare with: ${compare}`;
          } else {
            let compare = tabFilters.compare_with;
            filtersTitle = `NAICS: ${naicsCode}, Data Type: ${dataType}, Revenue: ${revenueBand}, Region: ${region}, Compare with: ${compare}`;
          }
          const dashboardPages = getDashBoardPagesPDF(props.reportMetrics.layout);
          return dashboardPages.map((dashboardItems) => (
            <div className="portrait-view">
              <div className="money-pdf-portrait">
                <div>
                  <div>
                    <p className="report-annual-title">Dashboard Benchmark Report</p>
                    <p className='report-filters-title'>{filtersTitle}</p>
                  </div>
                  <ParentDashboard
                    tabKey={tab.key}
                    reportBuilder={false}
                    aggregateData={aggregateData}
                    aggregateYTDDate={props.aggregateYTDDate}
                    calcsAvgType={calcsAvgType}
                    clientName={props.clientName}
                    companyYTDDate={props.companyYTDDate}
                    compareWith={tabFilters.compare_with}
                    dashboardLayout={dashboardItems}
                    displayColumnsBy={tabFilters.display_columns_by}
                    monthlyCompareWithTitle={monthlyCompareWithTitle}
                    forecastName={realForecastName}
                    forecastVals={forecastVals}
                    myNotes={props.moneyNotes}
                    handleMetricNotes={props.handleMetricNotes}
                    handleMoneyNotes={props.handleMoneyNotes}
                    handleOnLayoutChange={props.handleOnLayoutChange}
                    handleToggleDraggable={props.handleToggleDraggable}
                    calendarYearEnd={props.calendarYearEnd}
                    fiscalYearEnd={props.fiscalYearEnd}
                    monthLabels={monthLabels}
                    naicsCode={props.naicsCode}
                    page={props.page}
                    pdf
                    quarterMonths={props.quarterMonths}
                    reportPeriod={tabFilters.report_period}
                    tabFilters={tabFilters}
                    trendData={props.trendData}
                    tree={props.tree}
                    year={tabFilters.year}
                    yearRange={yearRange}
                  />
                </div>
                <p className="pdf-disclaimer-text">{`These financial statements have been prepared by ${firmName}, and have not been subjected to an audit or review or compilation engagement, and no assurance is provided on them. These financial statements are intended for management’s internal use only. Substantially all disclosures omitted.`}</p>
              </div>
            </div>
          ));
        }
        return (
          <>
            {props.activeTab.key === 'dashboard' && (
              <div>
                <DashboardViewControls
                  report="money"
                  dashboardViewModalState={props.dashboardViewModalState}
                  getDashBoardView={props.getDashBoardView}
                  handleDashboardViewModal={props.handleDashboardViewModal}
                />
              </div>
            )}
            {props.activeTab.key === 'dashboard' && (
              <ParentDashboard
                tabKey={tab.key}
                reportBuilder={false}
                aggregateData={aggregateData}
                aggregateYTDDate={props.aggregateYTDDate}
                calcsAvgType={calcsAvgType}
                clientName={props.clientName}
                companyYTDDate={props.companyYTDDate}
                compareWith={tabFilters.compare_with}
                dashboardLayout={props.reportMetrics.layout}
                displayColumnsBy={tabFilters.display_columns_by}
                monthlyCompareWithTitle={monthlyCompareWithTitle}
                forecastName={realForecastName}
                forecastVals={forecastVals}
                myNotes={props.moneyNotes}
                handleEditChartModal={props.handleEditChartModal}
                handleMetricNotes={props.handleMetricNotes}
                handleMoneyNotes={props.handleMoneyNotes}
                handleOnLayoutChange={props.handleOnLayoutChange}
                handleToggleDraggable={props.handleToggleDraggable}
                calendarYearEnd={props.calendarYearEnd}
                fiscalYearEnd={props.fiscalYearEnd}
                monthLabels={monthLabels}
                naicsCode={props.naicsCode}
                page={props.page}
                quarterMonths={props.quarterMonths}
                reportPeriod={tabFilters.report_period}
                tabFilters={tabFilters}
                trendData={props.trendData}
                tree={props.tree}
                year={tabFilters.year}
                yearRange={yearRange}
              />
            )}
          </>
        );

        break;
      case 'report_builder':
        return (
          <>
            {props.activeTab.key === 'report_builder' && (<div>
              <div className="dashboard-view-editor-header">
                <h1>Custom Report Builder</h1>
              </div>
              <DashboardViewModal
                adminEditor={false}
                editorPage={true}
                dashboardViewModalState={props.reportBuilderModalState}
                report="money"
                handleDashboardViewModal={props.handleReportBuilderModal}
                createDashBoardView={props.createCustomReport}
                deleteDashBoardView={props.deleteCustomReport}
                updateDashBoardView={props.updateCustomReport}
              />
              <ul id="nav-tabs-money-report" className="report-tab-list" role="tablist">
                <div>
                  <DashboardViewControls
                    editorPage
                    report="money"
                    dashboardViewModalState={props.reportBuilderModalState}
                    getDashBoardView={props.getCustomReport}
                    handleDashboardViewModal={props.handleReportBuilderModal}
                  />
                </div>
                <div className="edit-dashboard-section">
                  {props.reportBuilderMetrics.noChangesMade ? null : (
                    <button className="save-dashboard-btn" onClick={() => props.updateCustomReport()}>
                      Save Custom Report Changes
                    </button>
                  )}
                  <EditParentChildDashboard
                    allMetrics={props.reportBuilderMetrics}
                    editDashboard={props.editDashboard}
                    editorPage={false}
                    handleEditDashBoard={props.handleEditDashBoard}
                    handleEditMetrics={props.handleReportBuilderEditMetrics}
                    handleSelectAndLayout={props.handleReportBuilderSelectAndLayout}
                    page="money"
                  />
                </div>
              </ul>
            </div>)}
            {props.activeTab.key === 'report_builder' && (
              <ParentDashboard
                tabKey={tab.key}
                reportBuilder
                aggregateData={aggregateData}
                aggregateYTDDate={props.aggregateYTDDate}
                calcsAvgType={calcsAvgType}
                clientName={props.clientName}
                companyYTDDate={props.companyYTDDate}
                compareWith={tabFilters.compare_with}
                dashboardLayout={props.reportBuilderMetrics.layout}
                displayColumnsBy={tabFilters.display_columns_by}
                monthlyCompareWithTitle={monthlyCompareWithTitle}
                forecastName={realForecastName}
                forecastVals={forecastVals}
                myNotes={props.moneyNotes}
                handleEditChartModal={props.handleEditChartModal}
                handleMetricNotes={props.handleMetricNotes}
                handleMoneyNotes={props.handleMoneyNotes}
                handleOnLayoutChange={props.handleReportBuilderLayoutChange}
                handleToggleDraggable={props.handleToggleDraggable}
                calendarYearEnd={props.calendarYearEnd}
                fiscalYearEnd={props.fiscalYearEnd}
                monthLabels={monthLabels}
                naicsCode={props.naicsCode}
                page={props.page}
                quarterMonths={props.quarterMonths}
                reportPeriod={tabFilters.report_period}
                tabFilters={tabFilters}
                trendData={props.trendData}
                tree={props.tree}
                year={tabFilters.year}
                yearRange={yearRange}
              />
            )}
          </>
        );
        break;
      case 'people_financial_detail':
        if (tabFilters.report_period === 'Year to Date') {
          const monthlyCalcs = tabFilters.report_period === 'Trailing 12' ? trailing12 : monthly_calcs;
          const youSelectedYearTotalRevenue = monthlyCalcs?.you?.total_revenue?.[tabFilters.year];
          const youPreviousYearTotalRevenue = monthlyCalcs?.you?.total_revenue?.[tabFilters.year - 1];
          const firstYearMonth = getYTDMonthCount(youPreviousYearTotalRevenue, ytd_months)
          const secondYearMonth = getYTDMonthCount(youSelectedYearTotalRevenue, ytd_months)
          const compareFirstYearMonth = firstYearMonth < 10 ? '0' + firstYearMonth.toString() : firstYearMonth;
          const compareSecondYearMonth = secondYearMonth < 10 ? '0' + secondYearMonth.toString() : secondYearMonth;

          formattedFirstYearMonth = compareFirstYearMonth
          formattedSecondYearMonth = compareSecondYearMonth
        }
        return (
          <BigTableChartsForSheets
            key={tab.id}
            aggregateData={aggregateData}
            aggregateYTDDate={props.aggregateYTDDate}
            clientFirstYearMonth={formattedFirstYearMonth}
            clientSecondYearMonth={formattedSecondYearMonth}
            companyYTDDate={props.companyYTDDate}
            calendarYearEnd={props.calendarYearEnd}
            firmName={firmName}
            fiscalYearEnd={props.fiscalYearEnd}
            reportMetrics={props.reportMetrics}
            handleEditMetrics={props.handleEditMetrics}
            handleSelectAndLayout={props.handleSelectAndLayout}
            monthlyCompareWithTitle={monthlyCompareWithTitle}
            monthsObj={monthsObj}
            page="people"
            pdf={pdf}
            year={tabFilters.year}
            yearRange={yearRange}
            tabName={tab.name}
            tabFilters={tabFilters}
            tree={props.tree}
            clientName={props.clientName}
            n={props.n}
          />
        );
        break;
      case 'people_kpi': {
        const monthlyCalcs = tabFilters.report_period === 'Trailing 12' ? trailing12 : monthly_calcs;
        const aggCalcs = tabFilters.report_period === 'Trailing 12' ? aggregateData?.trailing12 : aggregateData?.monthly;
        let reportPeriod = tabFilters.display_columns_by === 'Months' ? 'Months' : tabFilters.report_period === 'Year to Date' ? 'Year to Date' : 'Years';
        const {
          youMetrics,
          compareMetrics,
          youPreviousYearMetricsYTD,
          comparePreviousYearMetricsYTD,
        } = kpiValues('people', tabFilters.year, monthlyCalcs, aggCalcs, reportPeriod, tabFilters.compare_with);
        return (
          <PeopleCustomersKPIs
            key={tab.id}
            aggregateData={aggregateData}
            aggregateYTDDate={props.aggregateYTDDate}
            calcs={calcs}
            monthly_calcs={monthlyCalcs}
            clientName={props.clientName}
            clientFirstYearMonth={formattedFirstYearMonth}
            clientSecondYearMonth={formattedSecondYearMonth}
            companyYTDDate={props.companyYTDDate}
            calendarYearEnd={props.calendarYearEnd}
            tabName={tab.name}
            tabFilters={tabFilters}
            firmName={firmName}
            fiscalYearEnd={props.fiscalYearEnd}
            reportMetrics={props.reportMetrics}
            handleEditMetrics={props.handleEditMetrics}
            handleSelectAndLayout={props.handleSelectAndLayout}
            monthlyCompareWithTitle={monthlyCompareWithTitle}
            monthsObj={monthsObj}
            n={props.n}
            pdf={pdf}
            page="people"
            year={tabFilters.year}
            yearRange={yearRange}
            youMetrics={youMetrics}
            compareMetrics={compareMetrics}
            youPreviousYearMetricsYTD={youPreviousYearMetricsYTD}
            comparePreviousYearMetricsYTD={comparePreviousYearMetricsYTD}
          />
        );
      }
      case 'customers_financial_detail':
        if (tabFilters.report_period === 'Year to Date') {
          const monthlyCalcs = tabFilters.report_period === 'Trailing 12' ? trailing12 : monthly_calcs;
          const youSelectedYearTotalRevenue = monthlyCalcs?.you?.total_revenue?.[tabFilters.year];
          const youPreviousYearTotalRevenue = monthlyCalcs?.you?.total_revenue?.[tabFilters.year - 1];
          const firstYearMonth = getYTDMonthCount(youPreviousYearTotalRevenue, ytd_months)
          const secondYearMonth = getYTDMonthCount(youSelectedYearTotalRevenue, ytd_months)
          const compareFirstYearMonth = firstYearMonth < 10 ? '0' + firstYearMonth.toString() : firstYearMonth;
          const compareSecondYearMonth = secondYearMonth < 10 ? '0' + secondYearMonth.toString() : secondYearMonth;

          formattedFirstYearMonth = compareFirstYearMonth
          formattedSecondYearMonth = compareSecondYearMonth
        }
        return (
          <BigTableChartsForSheets
            key={tab.id}
            aggregateData={aggregateData}
            aggregateYTDDate={props.aggregateYTDDate}
            clientFirstYearMonth={formattedFirstYearMonth}
            clientSecondYearMonth={formattedSecondYearMonth}
            companyYTDDate={props.companyYTDDate}
            calendarYearEnd={props.calendarYearEnd}
            firmName={firmName}
            fiscalYearEnd={props.fiscalYearEnd}
            reportMetrics={props.reportMetrics}
            handleEditMetrics={props.handleEditMetrics}
            handleSelectAndLayout={props.handleSelectAndLayout}
            monthlyCompareWithTitle={monthlyCompareWithTitle}
            monthsObj={monthsObj}
            page="customers"
            pdf={pdf}
            tabName={tab.name}
            tabFilters={tabFilters}
            year={tabFilters.year}
            yearRange={yearRange}
            tree={props.tree}
            clientName={props.clientName}
            n={props.n}
          />
        );
        break;
      case 'customers_kpi': {
        const monthlyCalcs = tabFilters.report_period === 'Trailing 12' ? trailing12 : monthly_calcs;
        const aggCalcs = tabFilters.report_period === 'Trailing 12' ? aggregateData?.trailing12 : aggregateData?.monthly;
        let reportPeriod = tabFilters.display_columns_by === 'Months' ? 'Months' : tabFilters.report_period === 'Year to Date' ? 'Year to Date' : 'Years';
        const {
          youMetrics,
          compareMetrics,
          youPreviousYearMetricsYTD,
          comparePreviousYearMetricsYTD,
        } = kpiValues('customers', tabFilters.year, monthlyCalcs, aggCalcs, reportPeriod, tabFilters.compare_with);
        return (
          <PeopleCustomersKPIs
            key={tab.id}
            aggregateData={aggregateData}
            aggregateYTDDate={props.aggregateYTDDate}
            calcs={calcs}
            monthly_calcs={monthlyCalcs}
            clientName={props.clientName}
            clientFirstYearMonth={formattedFirstYearMonth}
            clientSecondYearMonth={formattedSecondYearMonth}
            companyYTDDate={props.companyYTDDate}
            calendarYearEnd={props.calendarYearEnd}
            tabName={tab.name}
            tabFilters={tabFilters}
            firmName={firmName}
            fiscalYearEnd={props.fiscalYearEnd}
            reportMetrics={props.reportMetrics}
            handleEditMetrics={props.handleEditMetrics}
            handleSelectAndLayout={props.handleSelectAndLayout}
            monthlyCompareWithTitle={monthlyCompareWithTitle}
            monthsObj={monthsObj}
            n={props.n}
            pdf={pdf}
            page="customers"
            yearRange={yearRange}
            year={tabFilters.year}
            youMetrics={youMetrics}
            compareMetrics={compareMetrics}
            youPreviousYearMetricsYTD={youPreviousYearMetricsYTD}
            comparePreviousYearMetricsYTD={comparePreviousYearMetricsYTD}
          />
        );
      }
      default:
        if (tab.key.includes('default_') || tab.key.includes('custom_')) {
          let customLayout = props.customLayouts.find((c) => c.key === tab.key);

          if (props.pdf) {
            let naicsCode = tabFilters.code;
            let dataType = tabFilters.datatype;
            let revenueBand = tabFilters.revenue_band;
            let region = tabFilters.region;
            let filtersTitle;
            if (displayColumnsBy === 'Months' || reportPeriod === 'Year to Date') {
              const compareParts = tabFilters.compare_with.split(':');
              let compare = compareParts.length > 1 ? compareParts.slice(1).join(':').trim() : tabFilters.compare_with;
              filtersTitle = `NAICS: ${naicsCode}, Data Type: ${dataType}, Revenue: ${revenueBand}, Region: ${region}, Report Period: ${reportPeriod}, Compare with: ${compare}`;
            } else {
              let compare = tabFilters.compare_with;
              filtersTitle = `NAICS: ${naicsCode}, Data Type: ${dataType}, Revenue: ${revenueBand}, Region: ${region}, Compare with: ${compare}`;
            }
            const dashboardPages = getDashBoardPagesPDF(customLayout?.dashboard_layout || []);
            return dashboardPages.map((dashboardItems) => (
              <div className="portrait-view">
                <div className="money-pdf-portrait">
                  <div>
                    <div>
                      <p className="report-annual-title">{tab.name}</p>
                      <p className='report-filters-title'>{filtersTitle}</p>
                    </div>
                    <ParentDashboard
                      tabKey={tab.key}
                      customKey={tab.key}
                      customLayout
                      aggregateData={aggregateData}
                      aggregateYTDDate={props.aggregateYTDDate}
                      calcsAvgType={calcsAvgType}
                      clientName={props.clientName}
                      companyYTDDate={props.companyYTDDate}
                      compareWith={tabFilters.compare_with}
                      dashboardLayout={dashboardItems}
                      displayColumnsBy={tabFilters.display_columns_by}
                      monthlyCompareWithTitle={monthlyCompareWithTitle}
                      forecastName={realForecastName}
                      forecastVals={forecastVals}
                      myNotes={props.moneyNotes}
                      handleMetricNotes={props.handleMetricNotes}
                      handleMoneyNotes={props.handleMoneyNotes}
                      handleOnLayoutChange={props.handleCustomLayoutChange}
                      handleToggleDraggable={props.handleToggleDraggable}
                      calendarYearEnd={props.calendarYearEnd}
                      fiscalYearEnd={props.fiscalYearEnd}
                      monthLabels={monthLabels}
                      naicsCode={props.naicsCode}
                      page={props.page}
                      pdf
                      quarterMonths={props.quarterMonths}
                      reportPeriod={tabFilters.report_period}
                      tabFilters={tabFilters}
                      trendData={props.trendData}
                      tree={props.tree}
                      year={tabFilters.year}
                      yearRange={yearRange}
                    />
                  </div>
                  <p className="pdf-disclaimer-text">{`These financial statements have been prepared by ${firmName}, and have not been subjected to an audit or review or compilation engagement, and no assurance is provided on them. These financial statements are intended for management’s internal use only. Substantially all disclosures omitted.`}</p>
                </div>
              </div>
            ));
          }
          return (
            <>
              {props.activeTab.key === tab.key && (
                <ParentDashboard
                  tabKey={tab.key}
                  customKey={tab.key}
                  customLayout
                  aggregateData={aggregateData}
                  aggregateYTDDate={props.aggregateYTDDate}
                  calcsAvgType={calcsAvgType}
                  clientName={props.clientName}
                  companyYTDDate={props.companyYTDDate}
                  compareWith={tabFilters.compare_with}
                  dashboardLayout={customLayout?.dashboard_layout || []}
                  displayColumnsBy={tabFilters.display_columns_by}
                  monthlyCompareWithTitle={monthlyCompareWithTitle}
                  forecastName={realForecastName}
                  forecastVals={forecastVals}
                  myNotes={props.moneyNotes}
                  handleEditChartModal={props.handleEditChartModal}
                  handleMetricNotes={props.handleMetricNotes}
                  handleMoneyNotes={props.handleMoneyNotes}
                  handleOnLayoutChange={props.handleCustomLayoutChange}
                  handleToggleDraggable={props.handleToggleDraggable}
                  calendarYearEnd={props.calendarYearEnd}
                  fiscalYearEnd={props.fiscalYearEnd}
                  monthLabels={monthLabels}
                  naicsCode={props.naicsCode}
                  page="money"
                  quarterMonths={props.quarterMonths}
                  reportPeriod={tabFilters.report_period}
                  tabFilters={tabFilters}
                  trendData={props.trendData}
                  tree={props.tree}
                  year={tabFilters.year}
                  yearRange={yearRange}
                />
              )}
            </>
          );
        }
        break;
    }
  };

  const renderEditDashboard = () => {
    if (
      (!props.planAccess.money.dashboard && navbar.user_type !== 'Admin') ||
      (!navbar.reports_unlocked && props.planAccess.money.dashboard && navbar.user_type !== 'Admin')
    ) {
      return null;
    }

    const saveChangesButton = () => {
      if (props.activeTab.key.includes('default_') || props.activeTab.key.includes('custom_')) {
        const activeCustomLayout = props.customLayouts.find((c) => c.key === props.activeTab.key);
        const noChangesMade = activeCustomLayout ? activeCustomLayout.noChangesMade : true;
        return noChangesMade ? null : (
          <button
            className="save-dashboard-btn"
            onClick={() => props.updateCustomLayout(props.activeTab.key)}
          >
            Save Changes
          </button>
        );
      }
      return props.reportMetrics.noChangesMade ? null : (
        <button className="save-dashboard-btn" onClick={() => props.updateDashBoardView()}>
          Save Changes
        </button>
      );
    };

    if (
      (navbar.reports_unlocked && props.planAccess.money.dashboard) ||
      navbar.user_type === 'Admin'
    ) {
      let { noChangesMade } = props.reportMetrics;
      if (props.activeTab.key.includes('default_') || props.activeTab.key.includes('custom_')) {
        const activeCustomLayout = props.customLayouts.find((c) => c.key === props.activeTab.key);
        noChangesMade = activeCustomLayout?.noChangesMade;
      }
      return (
        <div className="edit-dashboard-section">
          {saveChangesButton()}
          <EditParentChildDashboard
            allMetrics={props.reportMetrics}
            editDashboard={props.editDashboard}
            handleEditDashBoard={props.handleEditDashBoard}
            handleEditMetrics={props.handleEditMetrics}
            handleSelectAndLayout={props.handleSelectAndLayout}
            page={props.page}
          />
        </div>
      );
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newTabs = Array.from(props.reportsOnTabs);
    const [movedTab] = newTabs.splice(result.source.index, 1);
    newTabs.splice(result.destination.index, 0, movedTab);
    props.saveTabOrder(newTabs);
  };

  function CoverPage() {
    const displayColumnsBy = filters.display_columns_by;
    const coverPeriod = '';
    const reportPeriod = filters.report_period;
    const reportTitle = 'Financial Statements';
    const NAICSfilter = filters.code;
    const numberOfDocs =
      naicsFocusedReport && filters.number_of_pros === 0
        ? 'All'
        : naicsFocusedReport
          ? filters.number_of_pros
          : '';
    const todayDate = moment(Date.now()).format('LL');

    return (
      <div className="portrait-view cover-page-pdf">
        <div className="cover-page-top-section" />
        <div className="cover-page-info">
          <div>
            <div className="img-title-page-print">
              {props.logo ? <img src={props.logo} alt="" /> : null}
            </div>
            <h1>{reportTitle}</h1>
            <div className="cp-report-info">
              <p>{props.name}</p>
              <p>INDUSTRY: {props.industry}</p>
              <p>NAICS: {props.naicsCode}</p>
              <p style={{ textTransform: 'uppercase' }}>{todayDate}</p>
            </div>
          </div>
        </div>
        <div className="cover-page-bottom-section">
          <p>Powered by Peerview Data</p>
        </div>
      </div>
    );
  }

  const toggleReportTabsPopover = () => {
    props.setReportTabsPopoverOpen((prev) => !prev);
  };

  if (props.pdf) {
    const dashboardPages = getDashBoardPagesPDF(props.reportMetrics.layout);
    const displayColumnsBy = filters.display_columns_by;
    let coverPeriod = '';
    const reportPeriod = filters.report_period;
    const reportTitle = 'Financial Statements';
    let NAICSfilter = filters.code;
    const numberOfDocs =
      naicsFocusedReport && filters.number_of_pros === 0
        ? 'All'
        : naicsFocusedReport
          ? filters.number_of_pros
          : '';
    const todayDate = moment(Date.now()).format('LL');

    // switch (displayColumnsBy) {
    // case 'Months':
    //   coverPeriod = props.year;
    //   break;
    // case 'Years':
    //   if (reportPeriod === 'Year to Date') {
    //     coverPeriod = `${props.year - 1} - ${props.year}`;
    //     break;
    //   } else {
    //     coverPeriod = `${props.yearRange[0]} - ${props.year}`;
    //     break;
    //   }
    // default:
    //   coverPeriod = `${props.yearRange[0]} - ${props.year}`;
    //   break;
    // }

    if (filterOptions.code && filterOptions.code.length > 1) {
      filterOptions.code.forEach((filter) => {
        if (NAICSfilter === filter[0]) NAICSfilter = `${filter[0]} - ${filter[1]}`;
      });
    }

    const portraitView = {
      backgroundColor: '#ffffff',
      width: '215mm !important',
      height: '279mm !important',
      margin: '0 auto',
      pageBreakInside: 'avoid',
      pageBreakAfter: 'always',
    };

    return (
      <div ref={ref} className="pdf-print-container">
        {props.displayPages.filter((tab) => tab.checked).map((tab) => getReportComponent(tab, true))}
      </div>
    );
  }

  const renderAllReports = () => {
    return (
      props.reportsOnTabs.map((tab) => {
        if (tab.key === 'dashboard') {
          return <React.Fragment key="dashboard">{getReportComponent(tab)}</React.Fragment>;
        }
        if (tab.key === 'report_builder') {
          return <React.Fragment key="report_builder">{getReportComponent(tab)}</React.Fragment>;
        }
        return (
          <div
            key={tab.key}
            className={props.activeTab.key === tab.key ? 'show-section' : 'hide-section'}
          >
            {getReportComponent(tab)}
          </div>
        );
      })
    )
  }

  return (
    <div className="money-report-component money-content">
      <VideoModal />
      <div style={{pointerEvents: props.loading ? 'none' : 'auto'}}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="tabs" direction="horizontal">
            {(provided) => (
              <ul
                id="nav-tabs-money-report"
                className="report-tab-list"
                role="tablist"
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {props.reportsOnTabs.map((tab, index) => {
                  const peopleTab = (tab?.report ?? '').includes('people');
                  const customerTab = (tab?.report ?? '').includes('customer');

                  const tabStyle = peopleTab
                    ? 'people-item'
                    : customerTab
                      ? 'customer-item'
                      : 'tab-item';
                  const activeTabStyle = peopleTab
                    ? 'people-active'
                    : customerTab
                      ? 'customer-active'
                      : 'active';
                  return (
                    <Draggable key={tab.key} draggableId={tab.key.toString()} index={index}>
                      {(provided) => (
                        <li
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          key={tab.key}
                          className={
                            props.activeTab.key === tab.key
                              ? `${tabStyle} ${activeTabStyle}`
                              : `${tabStyle}`
                          }
                          role="presentation"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleActiveTab(tab);
                          }}
                        >
                          <span className="tab-name">{tab.name}</span>
                          <button
                            type="button"
                            className="view-close-btn"
                            onClick={(e) => {
                              e.stopPropagation();

                              if (navbar.user_type === 'Admin' || navbar.reports_unlocked) {
                                if (tab.multiple_reports) {
                                  props.deleteReportTab(tab.id)
                                } else {
                                  props.hideReportFromTabs(tab.id);
                                }
                              } else if (!navbar.reports_unlocked && navbar.user_type !== 'Admin') {
                                openVideoModal('general')
                              }
                            }}
                          >
                            &times;
                          </button>
                        </li>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
                <div className="relative">
                  <button className="add-view-btn" ref={buttonRef} onClick={toggleReportTabsPopover}>
                    + Add
                  </button>
                  {props.reportTabsPopoverOpen && (
                    <ReportTabsPopover
                      ref={popoverRef}
                      hrefBuilder={hrefBuilder}
                      navbar={navbar}
                      entityType={props.entityType}
                      naicsCode={props.naicsCode}
                      allReportTabs={props.allReportTabs}
                      customDashboardViews={props.customDashboardViews}
                      defaultDashboardViews={props.defaultDashboardViews}
                      handleNewTabFormModal={props.handleNewTabFormModal}
                      handleActiveTab={handleActiveTab}
                      handleDefaultDashboardViewTab={props.handleDefaultDashboardViewTab}
                      subdomain={props.subdomain}
                    />
                  )}
                </div>
                {renderEditDashboard()}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      {
        props.loading 
          ? <div style={{ 'height': '70vh', 'display': 'flex', 'justifyContent': 'center', 'alignItems': 'center' }}><LoaderGraphic logoLoader={true} /></div> 
          : renderAllReports()
      }
      <div className="page-break" />
    </div>
  );
});

export default MoneyReport;
